import React, { useMemo, useState } from 'react';
import { FileText, Loader, Sigma } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { statementsFromSnapshot, buildCumulativeStatement } from '../../utils/exerciseStatements';

const fmt = (v) => `${(v || 0) >= 0 ? '+' : ''}${Math.round(v || 0).toLocaleString('fr-FR')} €`;

/**
 * Supervision — « Bilans d'exercice » de l'apprenant (21/09/2026).
 * Source : télémétrie (`yearly_statements` + `current_year_statement`).
 * Même PDF que celui généré par l'apprenant dans Finance › Historique.
 */
export default function ExerciseStatementsBlock({ snapshot, license }) {
  const { t } = useTranslation();
  const [includeCurrent, setIncludeCurrent] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const key = license?.license_key || 'x';

  const statements = useMemo(() => statementsFromSnapshot(snapshot, { includeCurrent }), [snapshot, includeCurrent]);
  const cumulative = useMemo(() => buildCumulativeStatement(statements), [statements]);
  const hasCurrent = !!snapshot?.current_year_statement;
  const synced = Array.isArray(snapshot?.yearly_statements);

  const download = async (statement, k) => {
    if (busyKey) return;
    setBusyKey(k);
    try {
      const { default: generate } = await import('../../utils/generateExerciseStatementPDF');
      generate({ statement, companyName: snapshot?.company_name, holderName: license?.holder_name, download: true });
    } catch (err) {
      console.error('[supervision] statement PDF failed', err);
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div data-testid={`statements-block-${key}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono flex items-center gap-1">
          <Sigma className="w-3 h-3" /> {t('sup_stmt_title')}
        </p>
        <div className="flex items-center gap-3">
          {hasCurrent && (
            <label className="flex items-center gap-1.5 text-[10px] text-slate-400 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={includeCurrent}
                onChange={(e) => setIncludeCurrent(e.target.checked)}
                className="accent-sky-400"
                data-testid={`stmt-include-current-${key}`}
              />
              {t('sup_stmt_include_current')}
            </label>
          )}
          <button
            type="button"
            onClick={() => download(cumulative, 'cumul')}
            disabled={!cumulative || !!busyKey}
            className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-sky-500/15 text-sky-300 border border-sky-500/30 hover:bg-sky-500/25 disabled:opacity-40 disabled:cursor-not-allowed font-semibold uppercase tracking-wider transition-colors"
            data-testid={`stmt-cumulative-pdf-${key}`}
          >
            {busyKey === 'cumul' ? <Loader className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
            {t('sup_stmt_cumulative_pdf')}
          </button>
        </div>
      </div>

      {!synced ? (
        <p className="text-[11px] text-slate-500" data-testid={`stmt-not-synced-${key}`}>{t('sup_stmt_not_synced')}</p>
      ) : statements.length === 0 ? (
        <p className="text-[11px] text-slate-500" data-testid={`stmt-empty-${key}`}>
          {hasCurrent ? t('sup_stmt_empty_with_current') : t('sup_stmt_empty')}
        </p>
      ) : (
        <ul className="space-y-1">
          {statements.map((s) => (
            <li
              key={s.year}
              className="flex items-center justify-between gap-2 text-[11px] px-2 py-1.5 rounded bg-ink-800/60 border border-ink-600"
              data-testid={`stmt-row-${key}-${s.year}`}
            >
              <span className="text-slate-200 font-semibold">
                {t('sup_stmt_year', { year: s.year })}
                {s.provisional && (
                  <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 font-mono uppercase">{t('sup_stmt_provisional')}</span>
                )}
              </span>
              <span className="flex items-center gap-3">
                <span className="font-mono text-slate-400">{t('sup_stmt_ca_short')} {Math.round(s.revenue).toLocaleString('fr-FR')} €</span>
                <span className={`font-mono font-bold ${s.net >= 0 ? 'text-money' : 'text-danger'}`}>{fmt(s.net)}</span>
                <button
                  type="button"
                  onClick={() => download(s, s.year)}
                  disabled={!!busyKey}
                  className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-200 border border-amber-500/30 hover:bg-amber-500/25 disabled:opacity-40 font-semibold uppercase tracking-wider transition-colors"
                  data-testid={`stmt-pdf-${key}-${s.year}`}
                >
                  {busyKey === s.year ? <Loader className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                  {t('sup_stmt_pdf')}
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
